import React, { useEffect, useState, useMemo } from "react";
import {
  CircularProgress,
  Button,
  Box,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  Tooltip,
  Paper,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SchoolIcon from "@mui/icons-material/School";
import PeopleIcon from "@mui/icons-material/People";
import PaymentIcon from "@mui/icons-material/Payment";
import SettingsIcon from "@mui/icons-material/Settings";
import HistoryIcon from "@mui/icons-material/History";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import GroupIcon from "@mui/icons-material/Group";
import HandshakeIcon from "@mui/icons-material/Handshake";
import QuizIcon from "@mui/icons-material/Quiz";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import {
  get_school_api,
  get_school_overview_api,
  suspend_school_api,
  act_as_admin_api,
  delete_school_api,
} from "../../DAL/FlightSchools/FlightSchools";
import { delete_school_student_api } from "../../DAL/SchoolPortal/SchoolPortal";
import { baseUrl } from "../../config/config";
import { useGodMode } from "../../Hooks/GodModeContext";
import FlightSchoolStudents from "./FlightSchoolStudents";
import FlightSchoolBilling from "./FlightSchoolBilling";
import FlightSchoolSettings from "./FlightSchoolSettings";
import FlightSchoolAdmins from "./FlightSchoolAdmins";
import FlightSchoolTutors from "./FlightSchoolTutors";
import CourseTerms from "./CourseTerms";
import FlightSchoolAuditLog from "./FlightSchoolAuditLog";
import ConfirmationPopup from "../../components/GeneralComponents/ConfirmationPopup";

const StatCard = ({ label, value, icon, color = "#c01e2e" }) => (
  <Paper
    sx={{
      p: 2.5,
      backgroundColor: "#ffffff",
      border: "1px solid rgba(26,26,26,0.09)",
      borderRadius: "12px",
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
      display: "flex",
      alignItems: "center",
      gap: 2,
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: "11px",
        backgroundColor: `${color}18`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {React.cloneElement(icon, { sx: { color, fontSize: 22 } })}
    </Box>
    <Box>
      <Typography sx={{ color: "#aaa", fontSize: "11px", fontFamily: "'Rubik', sans-serif", textTransform: "uppercase", letterSpacing: "0.3px" }}>{label}</Typography>
      <Typography sx={{ color: "#1a1a1a", fontSize: "22px", fontWeight: 600, fontFamily: "'Rubik', sans-serif", lineHeight: 1.2 }}>{value}</Typography>
    </Box>
  </Paper>
);

const InfoItem = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography sx={{ color: "#aaa", fontSize: "11px", mb: 0.5, fontFamily: "'Rubik', sans-serif", textTransform: "uppercase", letterSpacing: "0.3px" }}>{label}</Typography>
    <Typography sx={{ color: "#1a1a1a", fontSize: "14px", fontFamily: "'Rubik', sans-serif" }}>{value || "—"}</Typography>
  </Box>
);

const PersonRow = ({ firstName, lastName, email, chipLabel, chipSx, chipColor, lastActive }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      py: 1.25,
      borderBottom: "1px solid rgba(26,26,26,0.06)",
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
      <Avatar sx={{ width: 30, height: 30, backgroundColor: "#c01e2e", fontSize: "11px", flexShrink: 0 }}>
        {firstName?.[0]}{lastName?.[0]}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: "#1a1a1a", fontSize: "13px", fontWeight: 500, fontFamily: "'Rubik', sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {`${firstName} ${lastName}`.trim() || email?.split("@")[0] || "Unknown"}
        </Typography>
        <Typography sx={{ color: "#aaa", fontSize: "11px", fontFamily: "'Rubik', sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {email}
        </Typography>
      </Box>
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
      <Chip
        label={chipLabel}
        size="small"
        color={chipColor}
        sx={{ borderRadius: "6px", fontSize: "10px", textTransform: "capitalize", height: 20, ...chipSx }}
      />
      <Typography sx={{ color: "#aaa", fontSize: "10px", fontFamily: "'Rubik', sans-serif", whiteSpace: "nowrap" }}>
        {lastActive}
      </Typography>
    </Box>
  </Box>
);

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const getStatusColor = (status) => {
  switch (status) {
    case "active":
      return "success";
    case "suspended":
      return "warning";
    case "pending":
      return "info";
    case "expired":
    case "terminated":
      return "error";
    default:
      return "default";
  }
};

const getRoleColor = (role) => {
  switch (role) {
    case "primary_admin": return { bg: "rgba(192, 30, 46, 0.2)", color: "#c01e2e" };
    case "staff_admin": return { bg: "rgba(69, 183, 209, 0.2)", color: "#45B7D1" };
    case "instructor": return { bg: "rgba(34, 197, 94, 0.2)", color: "#22c55e" };
    case "operations": return { bg: "rgba(245, 158, 11, 0.2)", color: "#f59e0b" };
    default: return { bg: "rgba(26,26,26,0.07)", color: "#6b6b6b" };
  }
};

const formatRole = (role) => {
  if (!role) return "Staff";
  return role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const getPartnerLabel = (partnerConfig) => {
  if (!partnerConfig || partnerConfig.type === "standard") return null;
  const { type, durationDays, partnerExpiryDate, hasExpired } = partnerConfig;
  if (type === "free_trial") {
    const expiry = partnerExpiryDate ? moment(partnerExpiryDate).format("DD MMM YYYY") : null;
    const suffix = hasExpired ? " (expired)" : expiry ? ` (expires ${expiry})` : "";
    return `Free Trial — ${durationDays || "?"} days${suffix}`;
  }
  if (type === "auto_conversion") {
    const expiry = partnerExpiryDate ? moment(partnerExpiryDate).format("DD MMM YYYY") : null;
    const suffix = hasExpired ? " (converted)" : expiry ? ` (converts ${expiry})` : "";
    return `Auto Conversion — ${durationDays || "?"} days${suffix}`;
  }
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

export default function FlightSchoolDetail() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { school_id } = useParams();
  const { enterGodMode } = useGodMode();

  const [isLoading, setIsLoading] = useState(true);
  const [school, setSchool] = useState(null);
  const [overview, setOverview] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  // What this school's region sells. Derived here rather than fetched — the
  // school document already carries its region and its enabled courses.
  const courseCatalogue = useMemo(() => {
    const LABELS = { UK_PPL: "PPL", UK_ATPL: "ATPL", FAA_PPL: "Private Pilot", KCAA_PPL: "Kenya PPL" };
    const BY_REGION = { UK: ["UK_PPL", "UK_ATPL"], US: ["FAA_PPL"], KE: ["KCAA_PPL"] };
    const enabled = (school?.tutorsConfig?.courses || []).filter((c) => c.enabled !== false).map((c) => c.license);
    const list = enabled.length ? enabled : (BY_REGION[school?.region] || BY_REGION.UK);
    return list.map((license) => ({ license, label: LABELS[license] || license }));
  }, [school]);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadSchoolData();
    loadOverview();
  }, [school_id]);

  const loadSchoolData = async () => {
    try {
      const result = await get_school_api(school_id);
      if (result.code === 200) {
        setSchool(result.school);
        setIsLoading(false);
      } else {
        enqueueSnackbar(result.message || "Failed to load school", { variant: "error" });
        navigate("/flight-schools");
      }
    } catch (error) {
      enqueueSnackbar("Error loading school data", { variant: "error" });
      navigate("/flight-schools");
    }
  };

  const loadOverview = async () => {
    try {
      const result = await get_school_overview_api(school_id);
      if (result.code === 200) setOverview(result);
    } catch (e) {}
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSuspend = async (reason) => {
    try {
      const result = await suspend_school_api(school_id, reason);
      if (result.code === 200) {
        enqueueSnackbar("Flight school suspended", { variant: "success" });
        loadSchoolData();
      } else {
        enqueueSnackbar(result.message || "Failed to suspend", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Error suspending school", { variant: "error" });
    }
    setConfirmDialog({ open: false, action: null });
  };

  const handleActAsAdmin = async () => {
    try {
      const result = await act_as_admin_api(school_id);
      if (result.code === 200) {
        enterGodMode(school);
        navigate("/school/dashboard");
      } else {
        enqueueSnackbar(result.message || "Failed to enter god mode", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Error entering admin mode", { variant: "error" });
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const students = overview?.students || [];
      if (students.length > 0) {
        const migrationResults = await Promise.allSettled(
          students.map((student) => delete_school_student_api(student._id))
        );
        const failed = migrationResults.filter(
          (r) => r.status === "rejected" || (r.status === "fulfilled" && r.value.code !== 200 && !r.value.success)
        );
        if (failed.length > 0) {
          enqueueSnackbar(`${failed.length} student(s) failed to migrate. School was not deleted.`, { variant: "error" });
          setIsDeleting(false);
          return;
        }
      }

      const result = await delete_school_api(school_id);
      if (result.code === 200 || result.success) {
        enqueueSnackbar("School deleted successfully", { variant: "success" });
        navigate("/flight-schools");
      } else {
        enqueueSnackbar(result.message || "Failed to delete school", { variant: "error" });
      }
    } catch (err) {
      enqueueSnackbar("Failed to delete school", { variant: "error" });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="circular-progress">
        <CircularProgress color="primary" />
      </div>
    );
  }

  const extraSeats = Math.max(0, (school.active_students_count || 0) - school.included_seats);
  const students = overview?.students || [];
  const staff = overview?.staff || [];
  const stats = overview?.stats || {};
  const partnerLabel = getPartnerLabel(school.partnerConfig);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, minHeight: "100vh", backgroundColor: "#FAF7F2", fontFamily: "'Rubik', sans-serif" }}>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton onClick={() => navigate("/flight-schools")} sx={{ color: "#6b6b6b", "&:hover": { color: "#c01e2e" } }}>
            <ArrowBackIcon />
          </IconButton>
          <Avatar
            src={school.branding?.logo ? baseUrl + school.branding.logo : undefined}
            sx={{
              width: 60,
              height: 60,
              backgroundColor: school.branding?.primary_color || "#c01e2e",
              border: "2px solid rgba(26,26,26,0.12)",
            }}
          >
            <SchoolIcon sx={{ fontSize: 30 }} />
          </Avatar>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography sx={{ color: "#1a1a1a", fontSize: "22px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>
                {school.name}
              </Typography>
              <Chip
                label={school.status || "active"}
                size="small"
                color={getStatusColor(school.status)}
                sx={{ borderRadius: "8px", textTransform: "capitalize" }}
              />
            </Box>
            <Typography sx={{ color: "#6b6b6b", fontSize: "14px", fontFamily: "'Rubik', sans-serif" }}>
              {school.primary_admin_email}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Tooltip title="Act as Admin (God Mode)">
            <Button
              variant="outlined"
              startIcon={<AdminPanelSettingsIcon />}
              onClick={handleActAsAdmin}
              sx={{
                borderColor: "#c01e2e",
                color: "#c01e2e",
                textTransform: "none",
                fontFamily: "'Rubik', sans-serif",
                borderRadius: "8px",
                "&:hover": { backgroundColor: "rgba(192,30,46,0.08)" },
              }}
            >
              God Mode
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/flight-schools/edit/${school_id}`)}
            sx={{
              borderColor: "rgba(26,26,26,0.2)",
              color: "#6b6b6b",
              textTransform: "none",
              fontFamily: "'Rubik', sans-serif",
              borderRadius: "8px",
              "&:hover": { borderColor: "#c01e2e", color: "#c01e2e", backgroundColor: "rgba(192,30,46,0.05)" },
            }}
          >
            Edit
          </Button>
          {school.status !== "suspended" && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<PauseCircleIcon />}
              onClick={() => setConfirmDialog({ open: true, action: "suspend" })}
            >
              Suspend
            </Button>
          )}
        </Box>
      </Box>

      {/* Stats Row */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(6, 1fr)" }, gap: 2, mb: 3 }}>
        <StatCard
          label="Active Students"
          value={school.active_students_count || 0}
          icon={<PeopleIcon />}
        />
        <StatCard
          label="Included Seats"
          value={school.included_seats}
          icon={<PeopleIcon />}
          color="#4caf50"
        />
        <StatCard
          label="Extra Seats"
          value={extraSeats}
          icon={<PeopleIcon />}
          color={extraSeats > 0 ? "#ff9800" : "#94a3b8"}
        />
        <StatCard
          label="Monthly Fee"
          value={`£${school.monthly_student_fee ?? 0}`}
          icon={<PaymentIcon />}
          color="#2196f3"
        />
        <StatCard
          label="Questions Generated"
          value={(stats.total_questions ?? 0).toLocaleString()}
          icon={<QuizIcon />}
          color="#8b5cf6"
        />
        <StatCard
          label="Last Active"
          value={stats.last_active_at ? moment(stats.last_active_at).fromNow() : "Never"}
          icon={<CalendarTodayIcon />}
          color="#06b6d4"
        />
      </Box>

      {/* Two-column layout */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, mb: 3 }}>
        {/* Quick Info Card */}
        <Paper
          sx={{
            p: 3,
            backgroundColor: "#ffffff",
            border: "1px solid rgba(26,26,26,0.09)",
            borderRadius: "12px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          }}
        >
          <Typography sx={{ color: "#1a1a1a", fontSize: "15px", fontWeight: 600, fontFamily: "'Rubik', sans-serif", mb: 2 }}>
            Quick Info
          </Typography>
          {partnerLabel && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ color: "#aaa", fontSize: "11px", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.3px", fontFamily: "'Rubik', sans-serif" }}>Partner Type</Typography>
              <Chip
                icon={<HandshakeIcon sx={{ fontSize: 14 }} />}
                label={partnerLabel}
                size="small"
                sx={{
                  backgroundColor: "rgba(139,92,246,0.1)",
                  color: "#7c3aed",
                  borderRadius: "7px",
                  fontSize: "12px",
                  fontFamily: "'Rubik', sans-serif",
                }}
              />
            </Box>
          )}
          <InfoItem
            label="AI Excess Charging"
            value={school.ai_settings?.charge_excess_usage ? "Enabled" : "Disabled"}
          />
          <InfoItem
            label="Created"
            value={school.createdAt ? moment(school.createdAt).format("DD MMM YYYY, HH:mm") : null}
          />
          <InfoItem
            label="Stripe Customer ID"
            value={
              school.stripeCustomerId
                ? school.stripeCustomerId.length > 24
                  ? school.stripeCustomerId.substring(0, 24) + "…"
                  : school.stripeCustomerId
                : null
            }
          />
        </Paper>

        {/* Students List */}
        <Paper
          sx={{
            p: 3,
            backgroundColor: "#ffffff",
            border: "1px solid rgba(26,26,26,0.09)",
            borderRadius: "12px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <PeopleIcon sx={{ color: "#c01e2e", fontSize: 18 }} />
            <Typography sx={{ color: "#1a1a1a", fontSize: "15px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>
              Students
            </Typography>
            <Chip label={students.length} size="small" sx={{ height: 20, fontSize: "11px", backgroundColor: "rgba(26,26,26,0.07)", color: "#6b6b6b", fontFamily: "'Rubik', sans-serif" }} />
          </Box>
          {students.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <PeopleIcon sx={{ fontSize: 44, color: "#ddd", mb: 1.5 }} />
              <Typography sx={{ color: "#aaa", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>No students yet</Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 300, overflowY: "auto", "&::-webkit-scrollbar": { width: 4 }, "&::-webkit-scrollbar-thumb": { background: "rgba(26,26,26,0.15)", borderRadius: 2 } }}>
              {students.map((student) => (
                <PersonRow
                  key={student._id}
                  firstName={student.first_name || ""}
                  lastName={student.last_name || ""}
                  email={student.email}
                  chipLabel={student.status || "active"}
                  chipColor={getStatusColor(student.status || "active")}
                  lastActive={
                    student.lastLogin
                      ? moment(student.lastLogin).fromNow()
                      : student.lastQuestionTimestamp
                      ? moment(student.lastQuestionTimestamp).fromNow()
                      : "Never"
                  }
                />
              ))}
            </Box>
          )}
        </Paper>
      </Box>

      {/* Staff List */}
      {staff.length > 0 && (
        <Paper
          sx={{
            p: 3,
            backgroundColor: "#ffffff",
            border: "1px solid rgba(26,26,26,0.09)",
            borderRadius: "12px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <AdminPanelSettingsIcon sx={{ color: "#c01e2e", fontSize: 18 }} />
            <Typography sx={{ color: "#1a1a1a", fontSize: "15px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>
              Staff
            </Typography>
            <Chip label={staff.length} size="small" sx={{ height: 20, fontSize: "11px", backgroundColor: "rgba(26,26,26,0.07)", color: "#6b6b6b", fontFamily: "'Rubik', sans-serif" }} />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            {staff.map((member) => {
              const roleColors = getRoleColor(member.role);
              return (
                <PersonRow
                  key={member._id}
                  firstName={member.firstName || member.first_name || ""}
                  lastName={member.lastName || member.last_name || ""}
                  email={member.email}
                  chipLabel={formatRole(member.role)}
                  chipSx={{ backgroundColor: roleColors.bg, color: roleColors.color }}
                  lastActive={member.lastLogin ? moment(member.lastLogin).fromNow() : "Never"}
                />
              );
            })}
          </Box>
        </Paper>
      )}

      {/* Tabs */}
      <Paper
        sx={{
          backgroundColor: "#ffffff",
          border: "1px solid rgba(26,26,26,0.09)",
          borderRadius: "12px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          p: 3,
          mb: 3,
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: "rgba(26,26,26,0.09)" }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTab-root": { color: "#6b6b6b", textTransform: "none", fontSize: "14px", fontFamily: "'Rubik', sans-serif" },
              "& .MuiTab-root.Mui-selected": { color: "#c01e2e" },
              "& .MuiTabs-indicator": { backgroundColor: "#c01e2e" },
            }}
          >
            <Tab icon={<PeopleIcon />} iconPosition="start" label="Students" />
            <Tab icon={<PaymentIcon />} iconPosition="start" label="Billing" />
            <Tab icon={<GroupIcon />} iconPosition="start" label="Admins" />
            <Tab icon={<MenuBookIcon />} iconPosition="start" label="Tutors" />
            <Tab icon={<SettingsIcon />} iconPosition="start" label="Settings" />
            <Tab icon={<HistoryIcon />} iconPosition="start" label="Audit Log" />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <FlightSchoolStudents schoolId={school_id} school={school} onUpdate={loadSchoolData} />
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          <FlightSchoolBilling school={school} />
        </TabPanel>
        <TabPanel value={activeTab} index={2}>
          <FlightSchoolAdmins schoolId={school_id} school={school} />
        </TabPanel>
        <TabPanel value={activeTab} index={3}>
          <FlightSchoolTutors schoolId={school_id} school={school} />
        </TabPanel>
        <TabPanel value={activeTab} index={4}>
          {/* XAir's control, not the school's — it is a term of their contract,
              and a school able to extend its own students' access could give
              away what it is billed for. The update endpoint already refuses
              tutorsConfig from anyone who is not a super admin. */}
          <Box sx={{ mb: 3, pb: 3, borderBottom: "1px solid #E8E4DF" }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#1a1a1a", mb: 0.5 }}>
              Course access terms
            </Typography>
            <CourseTerms
              school={school}
              catalogue={courseCatalogue}
              onSaved={loadSchoolData}
            />
          </Box>

          <FlightSchoolSettings schoolId={school_id} school={school} onUpdate={loadSchoolData} />
        </TabPanel>
        <TabPanel value={activeTab} index={5}>
          <FlightSchoolAuditLog schoolId={school_id} />
        </TabPanel>
      </Paper>

      {/* Danger Zone */}
      <Paper
        sx={{
          p: 3,
          backgroundColor: "rgba(239, 68, 68, 0.05)",
          border: "1px solid rgba(239, 68, 68, 0.2)",
          borderRadius: "12px",
          mb: 3,
        }}
      >
        <Typography sx={{ color: "#ef4444", fontSize: "16px", fontWeight: 600, mb: 2 }}>
          Danger Zone
        </Typography>
        {showDeleteConfirm ? (
          <Box>
            <Typography sx={{ color: "#6b6b6b", fontSize: "14px", mb: 2, fontFamily: "'Rubik', sans-serif" }}>
              Are you sure you want to delete <strong style={{ color: "#1a1a1a" }}>{school.name}</strong>?
              {students.length > 0 && <> All {students.length} student(s) will be migrated to individual member accounts.</>}
              {" "}This action cannot be undone.
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleDelete}
                disabled={isDeleting}
                startIcon={<DeleteIcon />}
                sx={{ backgroundColor: "#ef4444", "&:hover": { backgroundColor: "#dc2626" } }}
              >
                {isDeleting ? "Deleting..." : "Yes, Delete School"}
              </Button>
              <Button
                variant="outlined"
                onClick={() => setShowDeleteConfirm(false)}
                sx={{ borderColor: "rgba(26,26,26,0.2)", color: "#6b6b6b", textTransform: "none", fontFamily: "'Rubik', sans-serif", borderRadius: "8px" }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => setShowDeleteConfirm(true)}
              startIcon={<DeleteIcon />}
              sx={{
                borderColor: "rgba(239,68,68,0.5)",
                color: "#ef4444",
                justifyContent: "flex-start",
                mb: 1.5,
                "&:hover": { borderColor: "#ef4444", backgroundColor: "rgba(239,68,68,0.1)" },
              }}
            >
              Delete School
            </Button>
            <Typography sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>
              Permanently delete this flight school and all associated data.
            </Typography>
          </>
        )}
      </Paper>

      {/* Confirmation Dialog */}
      <ConfirmationPopup
        openDelete={confirmDialog.open}
        setOpenDelete={(open) => setConfirmDialog({ ...confirmDialog, open })}
        handleAgree={handleSuspend}
        title={`Are you sure you want to suspend ${school.name}? All students will lose access.`}
        reasonField
        reasonRequired
        reasonLabel="Reason for suspension"
      />
    </Box>
  );
}
